import React, { useContext } from 'react';
import css from 'styled-jsx/css';
import { Form, Input, DatePicker, InputNumber, Select, Checkbox, Radio, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import DocumentInput from './DocumentInput/DocumentInput';
import CustomIcon from '../CustomIcon/CustomIcon';
import { LanguageContext } from '../../shared/contexts/Language.context';
import { ThemeContext } from '../../shared/contexts/Theme.context';
import { isControlOrModifier, isNumber } from '../../shared/utils/keydown';
import dayjs from 'dayjs';
import DocumentCapture from './DocumentCapture/DocumentCapture';
import { normalizeText } from '../../shared/utils/utils';
import CustomPhoneInput from './CustomPhoneInput/CustomPhoneInput';
import UploadDocumentInput from './UploadDocument/UploadDocumentInput';
import CameraInput from './CameraInput/CameraInput';
import { BookingContext } from '../../shared/contexts/Booking.context';
import getCountryISO2 from 'country-iso-3-to-2';
import defaultStyles, { global as defaultGlobal } from './FormItem.style';
import tauStyles, { global as tauGlobal } from './FormItem.TAU.style';

const ADULT_MIN_YEARS_AGE = 12;
const CHILD_MAX_YEARS_AGE = 21;

const hotelSelectorKey = 'hotelId';
const nationalityKey = 'nationality';
const secondSurnameKey = 'second_surname';
const expirationDateKey = 'expiration_date';
const phoneKey = 'phone';
const photoBackKey = 'id_photo_back';
const birthDateKey = 'birth_date';
const expeditionDateKey = 'expedition_date';
const surnameKey = 'surname';
const guestNationalityKey = 'guest_nationality';
const countryKey = 'country';
const birthCountryKey = 'birthCountry';
const documentIssuanceCountryCamelKey = 'documentIssuanceCountry';
const countrySelectorKeys = [
  nationalityKey,
  guestNationalityKey,
  countryKey,
  birthCountryKey,
  documentIssuanceCountryCamelKey,
  'document_issuance_country',
];

export default function FormItem({
  field,
  currentValues,
  setFileValue,
  hideIcon = false,
  hideBorder = false,
  formErrors = {},
  wrapFormItem = children => <>{children}</>,
  aliceSuccessAction,
  setPhonePrefix,
  phoneNumber,
  defaultCountry,
  checkinGuestId,
  setCurrentFormValue,
  onEmailBlur,
  guestType,
  useTauStyles = false,
  highlightedCountries: highlightedCountriesProp,
}) {
  const styles = useTauStyles ? tauStyles : defaultStyles;
  const global = useTauStyles ? tauGlobal : defaultGlobal;

  const {
    actions: { translate },
  } = useContext(LanguageContext);

  const { state: theme } = useContext(ThemeContext);
  const { className: themeClassName, styles: themeStyle } = styles(theme);
  const {
    state: { hotelId , checkinRequirements, highlightedCountries: contextHighlightedCountries },
  } = useContext(BookingContext);

  const { input, rules, nested, props } = inputByType(
    field,
    currentValues,
    setFileValue,
    translate,
    wrapFormItem,
    theme.isAliceEnabled,
    aliceSuccessAction,
    setPhonePrefix,
    phoneNumber,
    defaultCountry,
    checkinGuestId,
    setCurrentFormValue,
    onEmailBlur,
    guestType,
    theme.isKiosk,
    hotelId,
    themeClassName,
    useTauStyles,
    checkinRequirements,
    highlightedCountriesProp,
    contextHighlightedCountries
  );
  const imageDoc = isImageDocument(field.type);

  let style = `form-label`;
  if (field.type === 'document' && field.label.length > 180) {
    style += ' form-label-document';
  }

  const emailInfoText = theme?.guestInfo?.['email-info'];
  const showEmailInfoTooltip = field.type === 'email' && !!emailInfoText;

  const item = (
    <Form.Item
      label={
        field.type != 'checkbox' &&
        field.label &&
        (!theme.isAliceEnabled || !imageDoc) && (
          <span className={`${themeClassName} ${style}`}>
            {field.label}
            {showEmailInfoTooltip && (
              <Tooltip
                placement="right"
                title={
                  <span dangerouslySetInnerHTML={{ __html: emailInfoText }} />
                }
                overlayInnerStyle={{
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                }}
              >
                <InfoCircleOutlined
                  className="email-info-icon"
                  style={{ marginLeft: 6, fontSize: 13, color: '#4a6fa5', cursor: 'pointer' }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              </Tooltip>
            )}
          </span>
        )
      }
      name={field.key}
      key={field.key}
      rules={
        rules || [{ required: field.required, message: translate('guestForm.mandatoryField') }]
      }
      labelCol={{ span: 24 }}
      {...props}
      validateStatus={formErrors[field.key] && formErrors[field.key].status}
      help={formErrors[field.key] && formErrors[field.key].message}
      className={field.type.includes('radio-pre') ? 'inversed-form-item hide-required-mark' : ''}
    >
      {input}
    </Form.Item>
  );

  const existsOcrDescription = theme?.guestInfo && theme.guestInfo['ocr-desc'];
  return (
    <>
      {wrapFormItem(
        <div className={`${themeClassName} form-item-container ${hideBorder && 'no-border'}`}>
          {showIcon(field.key) && !hideIcon ? (
            <>
              <div className={`${themeClassName} icon-container ${imageDoc ? 'align-top' : ''}`}>
                <CustomIcon type={field.key}></CustomIcon>
              </div>
              <div className={`${themeClassName} item-container`}>
                {field.type === 'document_front' ? (
                  existsOcrDescription ? (
                    <p className={`${themeClassName} image-doc`}>{theme.guestInfo['ocr-desc']}</p>
                  ) : (
                    !field.required && (
                      <p className={`${themeClassName} image-doc`}>
                        {translate('guestForm.optionalDoc')}
                      </p>
                    )
                  )
                ) : (
                  ''
                )}
                {item}
              </div>
            </>
          ) : (
            <div className={`${themeClassName} no-icon-item-container`}>{item}</div>
          )}
        </div>
      )}
      {nested}
      {themeStyle}
      <style jsx global>
        {global}
      </style>
      <style jsx global>
        {`
          .inversed-form-item {
            display: flex;
            flex-direction: column-reverse;
            align-items: flex-start;
          }
          .hide-required-mark .ant-form-item-required:before {
            display: none !important;
          }
          .ant-radio-checked .ant-radio-inner {
            border-color: ${theme.mainColor} !important;
          }

          .ant-radio-checked .ant-radio-inner:after {
            background-color: ${theme.mainColor} !important;
          }

          .ant-radio:hover .ant-radio-inner {
            border-color: ${theme.mainColor} !important;
            border-color: red;
          }

          .highlighted-country-option {
            font-weight: 600;
            background-color: #f0f5ff !important;
          }
          .highlighted-country-option:last-of-type {
            border-bottom: 1px solid #d9d9d9;
            margin-bottom: 4px;
            padding-bottom: 8px;
          }
        `}
      </style>
    </>
  );
}

function showIcon(key) {
  const noIcon = [
    'data-privacy-policy',
    'data-commercial-use',
    'loyalty-program',
    'agency', // TODO(agency-filter): remove this once agency icon token is available.
  ];
  return noIcon.indexOf(key) === -1;
}

function isImageDocument(type) {
  const toTop = ['document_front', 'document_back'];
  return toTop.indexOf(type) > -1;
}

function inputByType(
  field,
  currentValues,
  setFileValue,
  translate,
  wrapFormItem,
  isAliceEnabled,
  aliceSuccessAction,
  setPhonePrefix,
  phoneNumber,
  defaultCountry,
  checkinGuestId,
  setCurrentFormValue,
  onEmailBlur,
  guestType,
  isKiosk,
  hotelId,
  themeClassName,
  useTauStyles,
  checkinRequirements,
  highlightedCountriesProp,
  contextHighlightedCountries
) {
  let nested = null;
  let input = null;
  let rules = null;
  let props = {};
  switch (field.type) {
    case 'text':
      if (field.key == phoneKey) {
        input = (
          <CustomPhoneInput
            hint={field.label}
            phoneNumber={phoneNumber}
            setPhonePrefix={setPhonePrefix}
            defaultCountry={defaultCountry}
          />
        );
      } else {
        input = <Input placeholder={field.placeholder} disabled={field.disabled} />;
      }
      break;
    case 'email':
      input = <Input placeholder={field.placeholder} onBlur={onEmailBlur} />;
      rules = [
        { required: field.required, type: 'email', message: translate('guestForm.invalidEmail') },
      ];
      break;
    case 'password':
      input = <Input.Password placeholder={field.placeholder} />;
      rules = [{ required: field.required, type: 'string' }];
      break;
    case 'date':
      input = (
        <DatePicker
          getPopupContainer={trigger => trigger.parentNode}
          placeholder={'DD/MM/YYYY'}
          format={'DD/MM/YYYY'}
          disabledDate={currentDate => disabledRangeForDateInput(field.key, currentDate, guestType)}
          inputReadOnly={false}
          defaultPickerValue={getDefaultDatePickerDate(field.key, guestType)}
        />
      );
      break;
    case 'numeric':
      if (field.key == phoneKey) {
        input = (
          <CustomPhoneInput
            hint={field.label}
            phoneNumber={phoneNumber}
            setPhonePrefix={setPhonePrefix}
            defaultCountry={defaultCountry}
          />
        );
      } else {
        input = <InputNumber />;
      }

      break;
    case 'select':
      const showSearch = true; //[nationalityKey, hotelSelectorKey].indexOf(field.key) > -1;
      const resolvedHighlightedCountries = field.highlightedCountries || highlightedCountriesProp || contextHighlightedCountries;
      const highlightedCountries = normalizeHighlightedCountries(resolvedHighlightedCountries);
      const highlightedCountriesMap = highlightedCountries.reduce((map, code, index) => {
        map[code] = index;
        const iso2 = getCountryISO2(code);
        if (iso2) map[iso2.toUpperCase()] = index;
        return map;
      }, {});

      const isCountrySelector =
        field.type === 'select' &&
        highlightedCountries.length > 0 &&
        countrySelectorKeys.includes(field.key);

      const sortedOptions = sortSelectOptions(field.options, highlightedCountriesMap, isCountrySelector);

      input = (
        <Select
          notFoundContent={translate('noData')}
          showSearch={showSearch}
          placeholder={field.placeholder}
          disabled={field.disabled}
          filterOption={(inputValue, option) => {
            return (
              normalizeText(option.children.toString()).indexOf(normalizeText(inputValue)) > -1
            );
          }}
          getPopupContainer={trigger => trigger.parentElement}
          popupMatchSelectWidth={false}
        >
          {sortedOptions
            .map((option, idx) => {
              if (
                option.fields &&
                (option.value == currentValues[field.key] ||
                  option.text == currentValues[field.key])
              ) {
                let fieldFilter = [secondSurnameKey, expirationDateKey];
                if (checkinRequirements?.chain && ['dom-pedro-lisboa-hotel', 'hoteles-rafael-civitfun'].includes(checkinRequirements.chain?.toLowerCase())) {
                  fieldFilter.push(surnameKey)
                }

                nested = option.fields.map(field =>
                  !fieldFilter.includes(field.key) &&
                  (!isAliceEnabled || field.key !== photoBackKey) ? (
                    <FormItem
                      key={field.key}
                      field={field}
                      currentValues={currentValues}
                      setFileValue={setFileValue}
                      wrapFormItem={wrapFormItem}
                      aliceSuccessAction={aliceSuccessAction}
                      checkinGuestId={checkinGuestId}
                      setCurrentFormValue={setCurrentFormValue}
                      onEmailBlur={onEmailBlur}
                      useTauStyles={useTauStyles}
                      highlightedCountries={highlightedCountries}
                    ></FormItem>
                  ) : null
                );
              }
              return (
                <Select.Option
                  value={option.value}
                  key={idx}
                  className={
                    isCountrySelector && highlightedCountriesMap[normalizeOptionValue(option.value)] !== undefined
                      ? 'highlighted-country-option'
                      : undefined
                  }
                >
                  {option.text}
                </Select.Option>
              );
            })}
        </Select>
      );
      const hasNullValues = field.options ? field.options.some(option => !option.value) : true;
      if (hasNullValues) {
        rules = [];
      }
      break;
    case 'checkbox':
      input = <Checkbox>{field.label}</Checkbox>;
      props.valuePropName = 'checked';
      break;
    case 'radio':
      input = (
        <Radio.Group className="form-item-radio-group">
          {field.options?.map((option, idx) => {
            return (
              <Radio value={option.value} key={idx}>
                {option.text}
              </Radio>
            );
          })}
        </Radio.Group>
      );
      props.valuePropName = 'value';
      break;
    case 'radio-pre':
      input = (
        <Radio.Group className="form-item-radio-group">
          {field.options?.map((option, idx) => {
            return (
              <Radio value={option.value} key={idx} className={`${themeClassName}`}>
                {option.text}
              </Radio>
            );
          })}
        </Radio.Group>
      );
      props.valuePropName = 'value';
      break;
    case 'document_front':
    case 'document_back':
      if (isAliceEnabled) {
        input = (
          <DocumentCapture
            currentValues={currentValues}
            aliceSuccessAction={aliceSuccessAction}
          ></DocumentCapture>
        );
      } else {
        input = <DocumentInput field={field} setFileValue={setFileValue}></DocumentInput>;
      }
      rules = [{ required: false }];
      break;
    case 'document':
      input =
        isKiosk &&
        (hotelId == '0738' ||
          hotelId == 3918 ||
          hotelId == 'hotel-test-ih' ||
          hotelId == 3252 ||
          hotelId == '5836' ||
          hotelId == 5240 ||
          hotelId == '6167' ||
          hotelId == 5255) ? (
          <CameraInput
            field={field}
            currentValues={currentValues}
            checkinGuestId={checkinGuestId}
            setCurrentFormValue={setCurrentFormValue}
          />
        ) : (
          <UploadDocumentInput
            field={field}
            currentValues={currentValues}
            checkinGuestId={checkinGuestId}
            setCurrentFormValue={setCurrentFormValue}
          />
        );
      rules = [
        {
          required: field.required,
          message: translate('guestForm.mandatoryField'),
          validator: (_rule, _value) => {
            if (!field.required || currentValues[field.key]) return Promise.resolve();
            else return Promise.reject(translate('guestForm.mandatoryField'));
          },
        },
      ];
      break;
    default:
      input = <div>Falta implementar "{field.type}"</div>;
  }
  return { input, rules, nested, props };

  function disabledRangeForDateInput(key, currentDate, guestType) {
    const yesterday = dayjs().subtract(1, 'day');

    switch (key) {
      case 'entranceDate':
        return (
          !currentDate.isSame(yesterday, 'day') &&
          !currentDate?.isSameOrAfter(dayjs().startOf('day'))
        );
      case 'departureDate':
        const entranceDate = currentValues && currentValues['entranceDate'];
        const today = dayjs();
        return entranceDate
          ? !currentDate?.isSameOrAfter(entranceDate)
          : !currentDate.isSameOrAfter(today, 'day');
      case birthDateKey:
        if (guestType === 'adult' || guestType === 'holder') {
          const minDate = dayjs().subtract(ADULT_MIN_YEARS_AGE, 'years');
          return !currentDate.isSameOrBefore(minDate);
        } else if (guestType === 'child') {
          const maxDate = dayjs().subtract(CHILD_MAX_YEARS_AGE, 'years');
          return !currentDate.isSameOrAfter(maxDate);
        }
        return false;
      case expirationDateKey:
        return !currentDate?.isSameOrAfter(dayjs().startOf('day'));
      case expeditionDateKey:
        return currentDate?.isSameOrAfter(dayjs().endOf('day'));
      default:
        return false;
    }
  }

  function getDefaultDatePickerDate(key, guestType) {
    if (key === birthDateKey && guestType === 'adult') {
      return dayjs().subtract(ADULT_MIN_YEARS_AGE, 'years');
    } else {
      return dayjs();
    }
  }

  function normalizeHighlightedCountries(highlightedCountries = []) {
    if (!Array.isArray(highlightedCountries)) return [];

    return highlightedCountries
      .filter(countryCode => typeof countryCode === 'string' && countryCode.trim())
      .map(countryCode => countryCode.trim().toUpperCase());
  }

  function normalizeOptionValue(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim().toUpperCase();
  }

  function normalizeOptionText(text) {
    const textValue = text === null || text === undefined ? '' : String(text);
    return normalizeText(textValue);
  }

  function sortSelectOptionEntries(optionA, optionB, highlightedCountriesMap, prioritizeHighlighted) {
    if (prioritizeHighlighted) {
      const highlightedIndexA = highlightedCountriesMap[normalizeOptionValue(optionA?.value)];
      const highlightedIndexB = highlightedCountriesMap[normalizeOptionValue(optionB?.value)];
      const isHighlightedA = highlightedIndexA !== undefined;
      const isHighlightedB = highlightedIndexB !== undefined;

      if (isHighlightedA && !isHighlightedB) return -1;
      if (!isHighlightedA && isHighlightedB) return 1;
      if (isHighlightedA && isHighlightedB) return highlightedIndexA - highlightedIndexB;
    }

    return normalizeOptionText(optionA?.text).localeCompare(normalizeOptionText(optionB?.text));
  }

  function sortSelectOptions(options = [], highlightedCountriesMap, prioritizeHighlighted) {
    if (!Array.isArray(options)) return [];

    return [...options].sort((optionA, optionB) =>
      sortSelectOptionEntries(optionA, optionB, highlightedCountriesMap, prioritizeHighlighted)
    );
  }
}
